import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  Chip,
  IconButton,
  Autocomplete,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { InventoryService } from '../inventory/services';
import AddInventoryItem from '../inventory/AddInventoryItem';
import GuideButton from '../../common/guide/GuideButton';

// Warehouse interface
interface Warehouse {
  id: string;
  name: string;
  location?: string;
  isDefault?: boolean;
}

// InvoiceItem tipi (PurchaseInvoice ile aynı)
interface InvoiceItem {
  id: string;
  itemId: string;
  itemName: string;
  quantity: number;
  unit: string;
  enteredPrice: number;
  priceType: 'VAT_EXCLUDED' | 'VAT_INCLUDED';
  unitPrice: number;
  unitPriceWithVat: number;
  vatRate: number;
  sctRate: number;
  discountRate: number;
  discountAmount: number;
  lineTotal: number;
  netTotal: number;
  vatAmount: number;
  sctAmount: number;
  grossTotal: number;
  warehouseId?: string;
  displayQuantity?: string;
}

// Backend'den gelen parse edilmiş ürün (MatchedInvoiceItemDto)
interface ParsedEInvoiceItem {
  parsedItem: {
    sequenceNumber: number;
    itemName: string;
    quantity: number;
    unit: string;
    unitPrice: number;
    vatRate: number;
    discountRate: number;
    lineNumber: number;
  };
  matchedItemId: string | null;
  matchedItemName: string | null;
  matchConfidence: number;
  isManualItem: boolean;
  matchDescription: string;
}

interface EInvoiceImportModalProps {
  open: boolean;
  onClose: () => void;
  onItemsLoaded: (items: InvoiceItem[]) => void;
  companyId: string;
  ettn: string;
  username: string;
  password: string;
  /// EDM gelen kutusu için hazır UBL XML içeriği (varsa QNB'den indirme atlanır)
  xmlContent?: string | null;
}

const EInvoiceImportModal: React.FC<EInvoiceImportModalProps> = ({
  open,
  onClose,
  onItemsLoaded,
  companyId,
  ettn,
  username,
  password,
  xmlContent
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [parsedItems, setParsedItems] = useState<ParsedEInvoiceItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [step, setStep] = useState<'loading' | 'review' | 'complete'>('loading');
  
  // Manuel eşleştirme için
  const [manualMatches, setManualMatches] = useState<{ [key: number]: { itemId: string | null, itemName: string | null } }>({});
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Stoksuz ürünler için
  const [stocklessItems, setStocklessItems] = useState<Set<number>>(new Set());
  
  // Yeni ürün ekleme modalı için
  const [addInventoryModalOpen, setAddInventoryModalOpen] = useState<boolean>(false);
  const [addingItemForIndex, setAddingItemForIndex] = useState<number | null>(null);
  const [prefillItemName, setPrefillItemName] = useState<string>('');
  
  // ✅ YENİ: Depo yönetimi
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [defaultWarehouseId, setDefaultWarehouseId] = useState<string | null>(null);
  const [itemWarehouses, setItemWarehouses] = useState<{ [key: number]: string }>({});

  // ✅ YENİ: Depoları yükle
  useEffect(() => {
    if (open && companyId) {
      loadWarehouses();
    }
  }, [open, companyId]);

  // ✅ YENİ: Varsayılan depoyu seç
  useEffect(() => {
    if (warehouses.length > 0 && !defaultWarehouseId) {
      const defaultWarehouse = warehouses.find(wh => wh.isDefault);
      if (defaultWarehouse) {
        setDefaultWarehouseId(defaultWarehouse.id);
        console.log('✅ [E-INVOICE] Varsayılan depo seçildi:', defaultWarehouse.name);
      } else {
        setDefaultWarehouseId(warehouses[0].id);
        console.log('✅ [E-INVOICE] İlk depo varsayılan olarak seçildi:', warehouses[0].name);
      }
    }
  }, [warehouses, defaultWarehouseId]);

  // ✅ YENİ: Parse edildiğinde her ürün için varsayılan depoyu set et
  useEffect(() => {
    if (parsedItems.length > 0 && defaultWarehouseId) {
      const newItemWarehouses: { [key: number]: string } = {};
      parsedItems.forEach((_, index) => {
        if (!itemWarehouses[index]) {
          newItemWarehouses[index] = defaultWarehouseId;
        }
      });
      if (Object.keys(newItemWarehouses).length > 0) {
        setItemWarehouses(prev => ({ ...prev, ...newItemWarehouses }));
        console.log('✅ [E-INVOICE] Tüm ürünler için varsayılan depo set edildi');
      }
    }
  }, [parsedItems, defaultWarehouseId]);

  // Modal açıldığında otomatik parse işlemini başlat
  useEffect(() => {
    if (open && ettn && username && password && companyId) {
      handleParseEInvoice();
    }
  }, [open, ettn, username, password, companyId]);

  // ✅ YENİ: Depoları yükle
  const loadWarehouses = async () => {
    try {
      const response = await accountingApi.get(ACCOUNTING_API.INVENTORY_WAREHOUSES);
      const result = response.data;
      const warehouseList = result.data || result || [];
      setWarehouses(warehouseList);
      console.log('✅ [E-INVOICE] Depolar yüklendi:', warehouseList.length);
    } catch (error) {
      console.error('❌ [E-INVOICE] Depo yükleme hatası:', error);
    }
  };

  // ✅ YENİ: Ürün için depo değiştir
  const handleWarehouseChange = (index: number, warehouseId: string) => {
    setItemWarehouses(prev => ({
      ...prev,
      [index]: warehouseId
    }));
    console.log('✅ [E-INVOICE] Ürün #', index, 'deposu değiştirildi:', warehouseId);
  };

  const handleParseEInvoice = async () => {
    try {
      setLoading(true);
      setError(null);
      setStep('loading');

      console.log('📧 [E-INVOICE] XML parse işlemi başlatılıyor:', { ettn, companyId });

      const response = await accountingApi.post(ACCOUNTING_API.INVOICE_PARSE_EINVOICE, {
          ettn: ettn,
          companyId: companyId,
          username: username,
          password: password,
          xmlContent: xmlContent || null
        });

      const result = response.data;

      console.log('📦 [E-INVOICE] API RESPONSE (RAW):', JSON.stringify(result, null, 2));
      console.log('📦 [E-INVOICE] result.success:', result.success);
      console.log('📦 [E-INVOICE] result.data:', result.data);

      if (!result.success) {
        throw new Error(result.message || 'E-fatura parse edilemedi');
      }

      console.log('✅ [E-INVOICE] Parse başarılı:', result);

      // ✅ Backend formatı: { success: true, data: { items: [...] } }
      const items: ParsedEInvoiceItem[] = result.data?.items || result.items || [];
      console.log('📦 [E-INVOICE] Bulunan ürün sayısı:', items.length);
      console.log('📦 [E-INVOICE] Items detayı:', items);
      setParsedItems(items);

      // Tüm ürünleri varsayılan olarak seçili yap
      const allIndexes = new Set(items.map((_, index) => index));
      setSelectedItems(allIndexes);

      setStep('review');

    } catch (err: any) {
      console.error('❌ [E-INVOICE] Parse hatası:', err);
      setError(err.message || 'E-fatura işlenirken bir hata oluştu');
      setStep('loading');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleItem = (index: number) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === parsedItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(parsedItems.map((_, index) => index)));
    }
  };

  // Manuel eşleştirme için stok ürünlerini ara
  const handleSearchInventory = async (searchText: string) => {
    if (!searchText || searchText.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await InventoryService.getPagedItemsSQL(
        companyId,
        1, // pageNumber
        20, // pageSize
        searchText, // searchTerm
        undefined, // categoryId
        undefined, // brandId
        undefined, // warehouseId
        true // isActive - sadece aktif ürünleri göster
      );

      setSearchResults(response.items);
    } catch (error) {
      console.error('❌ [E-INVOICE] Envanter arama hatası:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Manuel eşleştirme yap
  const handleManualMatch = (index: number, selectedItem: any | null) => {
    setManualMatches(prev => ({
      ...prev,
      [index]: selectedItem ? { itemId: selectedItem.id, itemName: selectedItem.name } : { itemId: null, itemName: null }
    }));
    setEditingRow(null);
    setSearchTerm('');
    setSearchResults([]);
  };

  // Eşleştirmeyi iptal et (Otomatik eşleştirmeye geri dön)
  const handleClearMatch = (index: number) => {
    // Manuel eşleştirmeyi tamamen kaldır
    setManualMatches(prev => {
      const newMatches = { ...prev };
      delete newMatches[index];
      return newMatches;
    });
    
    // Stoksuz işaretini de kaldır
    setStocklessItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
    
    // Düzenleme modunu kapat
    setEditingRow(null);
    setSearchTerm('');
    setSearchResults([]);
    
    console.log('🔄 [E-INVOICE] Eşleştirme iptal edildi, otomatik eşleştirmeye geri dönüldü:', index);
  };

  // Stoksuz ürün olarak işaretle
  const handleMarkAsStockless = (index: number) => {
    // Stoksuz olarak işaretle
    setStocklessItems(prev => new Set(prev).add(index));
    
    // Manuel eşleştirmeyi null yap (stoksuz)
    setManualMatches(prev => ({
      ...prev,
      [index]: { itemId: null, itemName: null }
    }));
    
    // Düzenleme modunu kapat
    setEditingRow(null);
    setSearchTerm('');
    setSearchResults([]);
    
    console.log('📦 [E-INVOICE] Stoksuz ürün olarak işaretlendi:', index);
  };

  // Satır düzenleme modunu aç/kapat
  const handleToggleEdit = (index: number) => {
    if (editingRow === index) {
      setEditingRow(null);
      setSearchTerm('');
      setSearchResults([]);
    } else {
      // Eğer stoksuz ürün ise, stoksuz işaretini kaldır (tekrar düzenleme yapabilsin)
      if (stocklessItems.has(index)) {
        setStocklessItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(index);
          return newSet;
        });
        console.log('🔄 [E-INVOICE] Stoksuz ürün düzenlemeye açıldı:', index);
      }
      
      setEditingRow(index);
      setSearchTerm('');
      setSearchResults([]);
    }
  };

  // Yeni ürün ekleme modalını aç
  const handleOpenAddInventoryModal = (index: number) => {
    const item = parsedItems[index];
    setAddingItemForIndex(index);
    setPrefillItemName(item.parsedItem.itemName); // E-faturadan gelen ürün adını pre-fill et
    setAddInventoryModalOpen(true);
  };

  // Yeni ürün ekleme modalını kapat ve otomatik eşleştir
  const handleCloseAddInventoryModal = () => {
    setAddInventoryModalOpen(false);
    
    // Modal kapandıktan sonra state'leri temizle
    setTimeout(() => {
      setAddingItemForIndex(null);
      setPrefillItemName('');
    }, 300);
  };

  // Yeni ürün başarıyla eklendikten sonra otomatik eşleştir
  const handleInventoryItemAdded = (newItemId: string, newItemName: string) => {
    if (addingItemForIndex !== null) {
      console.log(`✅ [E-INVOICE] Yeni ürün eklendi ve otomatik eşleştirildi: ${newItemName} (ID: ${newItemId})`);
      
      // Otomatik eşleştirme yap
      setManualMatches(prev => ({
        ...prev,
        [addingItemForIndex]: { itemId: newItemId, itemName: newItemName }
      }));
      
      // Düzenleme modunu kapat
      setEditingRow(null);
      setSearchTerm('');
      setSearchResults([]);
    }
    
    // Modal'ı kapat
    handleCloseAddInventoryModal();
  };

  const handleImport = () => {
    // Seçili ürünleri InvoiceItem formatına çevir
    const invoiceItems: InvoiceItem[] = [];

    selectedItems.forEach((index) => {
      const matchedItem = parsedItems[index];
      const { parsedItem, matchedItemId, matchedItemName } = matchedItem;

      // Stoksuz ürün kontrolü
      const isStockless = stocklessItems.has(index);
      
      // Manuel eşleştirme varsa onu kullan, yoksa otomatik eşleştirmeyi kullan
      const manualMatch = manualMatches[index];
      let finalItemId: string;
      let finalItemName: string | null;
      
      if (isStockless) {
        // Stoksuz ürün - özel ID ve e-faturadan gelen ürün adı
        finalItemId = `stockless_${Date.now()}_${index}`;
        finalItemName = null; // Stoksuz ürünler için itemName null
        console.log('📦 [E-INVOICE] Stoksuz ürün ekleniyor:', parsedItem.itemName);
      } else if (manualMatch !== undefined) {
        // Manuel eşleştirme var
        finalItemId = manualMatch.itemId || `manual_${Date.now()}_${index}`;
        finalItemName = manualMatch.itemName;
      } else {
        // Otomatik eşleştirme kullan
        finalItemId = matchedItemId || `manual_${Date.now()}_${index}`;
        finalItemName = matchedItemName;
      }

      // Backend'den gelen birim fiyat KDV Hariç
      const unitPrice = parsedItem.unitPrice;
      const quantity = parsedItem.quantity;
      const vatRate = parsedItem.vatRate;
      const discountRate = parsedItem.discountRate;

      // Hesaplamalar
      const lineTotal = quantity * unitPrice;
      const discountAmount = lineTotal * (discountRate / 100);
      const netTotal = lineTotal - discountAmount;
      const vatAmount = netTotal * (vatRate / 100);
      const sctAmount = 0; // E-faturada ÖTV genelde yok
      const grossTotal = netTotal + vatAmount + sctAmount;
      const unitPriceWithVat = unitPrice * (1 + vatRate / 100);

      const invoiceItem: InvoiceItem = {
        id: `einvoice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        itemId: finalItemId || `manual_${Date.now()}_${index}`,
        itemName: parsedItem.itemName,
        quantity: quantity,
        displayQuantity: quantity.toString().replace('.', ','),
        unit: parsedItem.unit,
        enteredPrice: unitPrice, // KDV Hariç
        priceType: 'VAT_EXCLUDED',
        unitPrice: unitPrice,
        unitPriceWithVat: unitPriceWithVat,
        vatRate: vatRate,
        sctRate: 0,
        discountRate: discountRate,
        discountAmount: discountAmount,
        lineTotal: lineTotal,
        netTotal: netTotal,
        vatAmount: vatAmount,
        sctAmount: sctAmount,
        grossTotal: grossTotal,
        warehouseId: itemWarehouses[index] || defaultWarehouseId || undefined // ✅ Seçilen depo
      };

      invoiceItems.push(invoiceItem);
    });

    console.log('✅ [E-INVOICE] Ürünler faturaya aktarılıyor:', invoiceItems);

    // Parent component'e gönder
    onItemsLoaded(invoiceItems);

    // Modal'ı kapat
    setStep('complete');
    setTimeout(() => {
      onClose();
      // State'leri temizle
      setParsedItems([]);
      setSelectedItems(new Set());
      setManualMatches({});
      setStocklessItems(new Set());
      setEditingRow(null);
      setSearchResults([]);
      setSearchTerm('');
      setAddInventoryModalOpen(false);
      setAddingItemForIndex(null);
      setPrefillItemName('');
      setItemWarehouses({}); // ✅ Depo state'lerini temizle
      setStep('loading');
      setError(null);
    }, 1000);
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      // State'leri temizle
      setParsedItems([]);
      setSelectedItems(new Set());
      setManualMatches({});
      setStocklessItems(new Set());
      setEditingRow(null);
      setSearchResults([]);
      setSearchTerm('');
      setAddInventoryModalOpen(false);
      setAddingItemForIndex(null);
      setPrefillItemName('');
      setItemWarehouses({}); // ✅ Depo state'lerini temizle
      setStep('loading');
      setError(null);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        disableEscapeKeyDown={loading}
      >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <CloudDownloadIcon color="primary" />
            <Typography variant="h6">E-Fatura Ürünlerini Aktar</Typography>
            <GuideButton guideKey="documents.eInvoiceImport" size="small" />
          </Box>
          {!loading && (
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Yükleme Aşaması */}
        {step === 'loading' && (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            {error ? (
              <>
                <WarningIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Bir hata oluştu
                </Typography>
                <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                  {error}
                </Alert>
              </>
            ) : (
              <>
                <CircularProgress size={64} sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  E-Fatura İşleniyor...
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  XML dosyası indiriliyor ve ürünler parse ediliyor
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  ETTN: {ettn}
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* İnceleme Aşaması */}
        {step === 'review' && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>{parsedItems.length} ürün</strong> bulundu. İçe aktarmak istediğiniz ürünleri seçin.
              </Typography>
            </Alert>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedItems.size === parsedItems.length && parsedItems.length > 0}
                        indeterminate={selectedItems.size > 0 && selectedItems.size < parsedItems.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Ürün Adı</TableCell>
                    <TableCell align="right">Miktar</TableCell>
                    <TableCell>Birim</TableCell>
                    <TableCell align="right">Birim Fiyat</TableCell>
                    <TableCell align="right">KDV %</TableCell>
                    <TableCell align="right">Toplam</TableCell>
                    <TableCell>Depo</TableCell>
                    <TableCell>Eşleşme</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedItems.map((item, index) => {
                    const isSelected = selectedItems.has(index);
                    const { parsedItem, matchedItemId, matchedItemName, matchConfidence } = item;
                    const total = parsedItem.quantity * parsedItem.unitPrice * (1 - parsedItem.discountRate / 100) * (1 + parsedItem.vatRate / 100);
                    
                    // Stoksuz ürün kontrolü
                    const isStockless = stocklessItems.has(index);
                    
                    // Manuel eşleştirme varsa onu göster
                    const manualMatch = manualMatches[index];
                    const displayMatchId = manualMatch !== undefined ? manualMatch.itemId : matchedItemId;
                    const displayMatchName = manualMatch !== undefined ? manualMatch.itemName : matchedItemName;
                    const isEditing = editingRow === index;

                    return (
                      <TableRow
                        key={index}
                        hover
                        selected={isSelected}
                        onClick={(e) => {
                          // Eğer Autocomplete veya butonlara tıklanmışsa satır seçimini yapma
                          if (!(e.target as HTMLElement).closest('.match-controls')) {
                            handleToggleItem(index);
                          }
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox checked={isSelected} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {parsedItem.itemName}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{parsedItem.quantity}</TableCell>
                        <TableCell>{parsedItem.unit}</TableCell>
                        <TableCell align="right">
                          {parsedItem.unitPrice.toFixed(2)} ₺
                        </TableCell>
                        <TableCell align="right">{parsedItem.vatRate}%</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {total.toFixed(2)} ₺
                          </Typography>
                        </TableCell>
                        <TableCell className="match-controls">
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value={itemWarehouses[index] || defaultWarehouseId || ''}
                              onChange={(e) => handleWarehouseChange(index, e.target.value)}
                              size="small"
                              sx={{ fontSize: '0.75rem' }}
                            >
                              {warehouses.map((warehouse) => (
                                <MenuItem key={warehouse.id} value={warehouse.id} sx={{ fontSize: '0.75rem' }}>
                                  {warehouse.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell className="match-controls">
                          {isEditing ? (
                            <Box sx={{ minWidth: 250 }}>
                              <Autocomplete
                                size="small"
                                options={searchResults}
                                getOptionLabel={(option) => option.name}
                                loading={searchLoading}
                                onInputChange={(_, value) => {
                                  setSearchTerm(value);
                                  handleSearchInventory(value);
                                }}
                                onChange={(_, newValue) => {
                                  handleManualMatch(index, newValue);
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Stok Ürünü Ara"
                                    variant="outlined"
                                    autoFocus
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment: (
                                        <>
                                          {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                          {params.InputProps.endAdornment}
                                        </>
                                      ),
                                    }}
                                  />
                                )}
                              />
                              <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  startIcon={<AddCircleIcon />}
                                  onClick={() => {
                                    setEditingRow(null); // Düzenleme modunu kapat
                                    handleOpenAddInventoryModal(index); // Yeni ürün modalını aç
                                  }}
                                >
                                  Yeni Ürün Ekle
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<ClearIcon />}
                                  onClick={() => handleClearMatch(index)}
                                >
                                  Eşleştirme İptal
                                </Button>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => handleMarkAsStockless(index)}
                                >
                                  Stoksuz Ürün Yap
                                </Button>
                              </Box>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {isStockless ? (
                                <Chip
                                  label="Stoksuz Ürün"
                                  color="secondary"
                                  size="small"
                                  icon={<WarningIcon />}
                                />
                              ) : displayMatchId ? (
                                <Chip
                                  label={manualMatch !== undefined ? `Manuel: ${displayMatchName}` : `Eşleşti: ${displayMatchName} (${matchConfidence}%)`}
                                  color={manualMatch !== undefined ? "primary" : (matchConfidence >= 80 ? "success" : "warning")}
                                  size="small"
                                  icon={<CheckCircleIcon />}
                                />
                              ) : (
                                <Chip
                                  label="Yeni Ürün"
                                  color="info"
                                  size="small"
                                  icon={<WarningIcon />}
                                />
                              )}
                              <IconButton
                                size="small"
                                onClick={() => handleToggleEdit(index)}
                                title="Eşleştirmeyi Değiştir"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {selectedItems.size} / {parsedItems.length} ürün seçildi
              </Typography>
            </Box>
          </Box>
        )}

        {/* Tamamlandı Aşaması */}
        {step === 'complete' && (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Ürünler Başarıyla Aktarıldı!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedItems.size} ürün faturaya eklendi
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === 'loading' && error && (
          <Button onClick={handleClose} variant="outlined">
            Kapat
          </Button>
        )}

        {step === 'review' && (
          <>
            <Button onClick={handleClose} variant="outlined">
              İptal
            </Button>
            <Button
              onClick={handleImport}
              variant="contained"
              color="primary"
              disabled={selectedItems.size === 0}
              startIcon={<CloudDownloadIcon />}
            >
              {selectedItems.size} Ürünü İçe Aktar
            </Button>
          </>
        )}
      </DialogActions>
      </Dialog>

      {/* Yeni Ürün Ekleme Modalı */}
      {addInventoryModalOpen && (
        <AddInventoryItem
          open={addInventoryModalOpen}
          onClose={handleCloseAddInventoryModal}
          onItemAdded={handleInventoryItemAdded}
          prefillName={prefillItemName}
        />
      )}
    </>
  );
};

export default EInvoiceImportModal;
